import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { TextField as MuiTextField } from '@material-ui/core';
import { Form } from '@unform/web';
import { parse } from 'date-fns';
import { isArray, isEmpty } from 'lodash';
import * as Yup from 'yup';

import { Input, Autocomplete, DateTime, Hidden } from '~/components/Form';
import { PesquisaTabela } from '~/components/Form/v2';
import { t, validCharacters } from '~/helpers';
import { compareIsAfterDates } from '~/helpers/dateTime';
import api from '~/services/api';

import { Container } from './styles';

export default function InicioPitStop({
  data,
  reference,
  close,
  cancelarAcao,
  setIsValidating,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const inputRef = React.useRef(null);
  const [inicioTurno, setInicioTurno] = React.useState(null);
  const [datahorainicialturno, setDatahorainicialturno] = React.useState(null);
  const [veiculo, setVeiculo] = React.useState([]);
  const [movimentacaoinicial, setMovimentacaoinicial] = React.useState(false);
  const [sempitstop, setSempitstop] = React.useState(false);
  const [initials, setInitials] = React.useState(data); // atencao - data
  const params = {};

  async function handleSubmit(param) {
    const formidveiculo = document.getElementById('idveiculo');
    if (formidveiculo.value) {
      if (veiculo && veiculo.idveiculo) {
        if (formidveiculo.value !== veiculo.idveiculo) {
          setVeiculo(null);
          setIsValidating(true);
          return;
        }
      } else {
        setIsValidating(true);
        return;
      }
    }
    try {
      const schema = Yup.object().shape({
        codigoatendimento: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        idveiculo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        dataencosteatendimento: !sempitstop
          ? Yup.mixed()
              .transform((value) =>
                value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
              )
              .required(t('s.geral.validacao.campoobrigatorio'))
              .test(
                'dataencosteatendimento',
                t(
                  's.cadastro.pitstop.datahoraatendimentodevesersersuperiorturno'
                ),
                () =>
                  compareIsAfterDates({
                    compare: datahorainicialturno,
                    current: param.dataencosteatendimento,
                  })
              )
          : Yup.string().nullable(),
        trematendimento: !sempitstop
          ? Yup.string()
              .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
              .nullable()
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                (value) => validCharacters(value)
              )
          : Yup.string().nullable(),
      });
      await schema.validate(param, {
        abortEarly: false,
      });
      const objSend = {
        ...param,
        idempresaveiculo: veiculo.idempresaveiculo,
        idcliente: client,
        idatendimento: null,
        datahorainicialturno: null,
        iddigitadorusuario: null,
        idmovimentacao: initials.idmovimentacao || null,
        origem: initials.origem || null,
        sempitstopatendimento: sempitstop ? 'S' : 'N',
      };
      await api.post('pitstop/inicio', objSend);
      await close();
      toast.success(t('s.geral.sucessoinclusao'));
    } catch (err) {
      if (err && isArray(err)) {
        toast.error(err[0]);
      }
      setIsValidating(false);
      await cancelarAcao();
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  const cellsVehicle = useMemo(
    () => [
      {
        id: 'idveiculo',
        label: t('s.configuracao.comboio.idveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.menu.cadastro.veiculo.modelo'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  async function handleCarregaTurno(idturno, idposto) {
    const { data } = await api.get(`/turnos/${idturno}/${idposto}`);
    setInicioTurno(
      `${data.datahorainicialturno} - Número: ${data.numeroturno}`
    );
    setDatahorainicialturno(data.datahorainicialturno);
  }

  useEffect(() => {
    // carrega dados turno
    handleCarregaTurno(initials.idturno, initials.idposto);
    //
    setDatahorainicialturno(initials.datahorainicialturno);
    setMovimentacaoinicial(initials.movimentacaoinicial || false);
    setSempitstop(
      initials.tipolocomotivaveiculo && initials.tipolocomotivaveiculo === 'O'
    );
  }, [initials]);

  useEffect(() => {
    if (veiculo) {
      setIsValidating(isEmpty(veiculo.idempresaveiculo));
    } else {
      setIsValidating(true);
    }
  }, [veiculo]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <Container>
        <Autocomplete
          name="idposto"
          label="codigonomeposto"
          url="postoscomboios"
          unique="idposto"
          title={t('s.geral.postocomboio')}
          disabled
          variant="outlined"
        />
        <Hidden name="idturno" />
        <MuiTextField
          label={t('s.menu.operacao.turno')}
          value={inicioTurno || ''}
          variant="outlined"
          size="small"
          disabled
          style={{ marginTop: 2, width: '100%' }}
        />
        <Input
          name="codigoatendimento"
          label={t('s.cadastro.pitstop.codigoatendimento')}
          inputRef={inputRef}
          type="number"
        />
        {!sempitstop && (
          <DateTime
            label={t('s.cadastro.pitstop.dataencosteatendimento')}
            name="dataencosteatendimento"
            serialize={(value) =>
              value
                ? value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                : null
            }
            time
            nullable
            disabled={sempitstop}
          />
        )}
        <div style={{ marginTop: 16 }}>
          <PesquisaTabela
            id="idveiculo"
            name="idveiculo"
            label={t('s.menu.cadastro.veiculo')}
            primary="idveiculo"
            secondary="nomemodeloveiculo"
            url="veiculos/locomotivas"
            header={cellsVehicle}
            getValue={(value) => {
              if (value && value?.idveiculo) {
                setVeiculo(value);
              } else {
                setVeiculo(null);
              }
            }}
            disabled={movimentacaoinicial}
          />
          {!sempitstop && (
            <Input
              name="trematendimento"
              label={t('s.cadastro.pitstop.trem')}
              // inputRef={inputRef}
              type="text"
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Container>
    </Form>
  );
}
